import React, { useEffect } from 'react';
import { useLibrary } from './hooks/useLibrary';
import { ProgressOverlay } from '../../common/progress-overlay/ProgressOverlay';
import styles from './scss/Library.module.scss';
import {
  Box,
  Button,
  Grid,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { useTranslation } from 'react-i18next';
import LibraryList from './components/library-list/LibraryList';
import { useSearchParams } from 'react-router-dom';
import OwnLibraryIcon from '../../../assets/svg/own-library-icon.svg?react';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import { selectAllDocumentTemplates } from '../../slices/document-templates/documentTemplatesSlice';
import ButtonDropDown from '../../common/button-drop-down/ButtonDropDown';
import MessageModal from '../../common/message-modal/MessageModal';
import DisclaimerText from '../../common/disclaimer-text/DisclaimerText';
import { acceptDisclaimer } from '../../slices/disclaimer/disclaimerSlice';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CardViewContent } from './components/card-view-content/CardViewContent';
import DashboardIcon from '../../../assets/svg/dashboard.svg?react';
import BallotIcon from '../../../assets/svg/ballot.svg?react';
import InputAdornment from '@mui/material/InputAdornment';
import CloseIcon from '@mui/icons-material/Close';
import { USER_PERMISSIONS } from '../../common/constants/permissions';
import { useCheckPermission } from '../../common/hooks/useCheckPermission';
import PreviewTooltip from './components/preview-block/PreviewTooltip';
import Preview from './components/preview-block/Preview';
import InfoIcon from '@mui/icons-material/Info';
import EmploymentInfoText from './components/employment-info-text/EmploymentInfoText';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';

const Library = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    libraryItems,
    handleTabChange,
    activeTab,
    pendingStatus: libraryItemsPendingStatus,
    setLibraryType,
    libraryType,
    region,
    setRegion,
    disclaimerMessage,
    setDisclaimerMessage,
    cardView,
    setCardView,
    setWorkflow,
    workflow,
    searchCleared,
    setSearchCleared,
    resetSearchFilter,
    employmentDataMessage,
    setEmploymentDataMessage,
    setSearchStringInputValue,
    searchStringInputValue,
  } = useLibrary();
  const documentTemplates = useAppSelector(selectAllDocumentTemplates);
  const checkPermission = useCheckPermission();
  const employeesLoading = useAppSelector((state) => state.employees.status);
  const customersLoading = useAppSelector((state) => state.customers.status);
  const suppliersLoading = useAppSelector((state) => state.suppliers.status);
  const disclaimerStatus = useAppSelector((state) => state.disclaimerInfo.status);
  const disclaimerInfoLoadingStatus = useAppSelector((state) => state.disclaimerInfo.status);
  const documentTemplatesLoading = useAppSelector((state) => state.documentTemplates.status);
  const sortByDate = (a: any, b: any) => {
    return Number(new Date(b.created)) - Number(new Date(a.created));
  };
  const regionsOptions = [
    { label: t('regions.au'), value: 'au' },
    { label: t('regions.uk'), value: 'uk' },
    { label: t('regions.global'), value: 'global' },
  ];

  const [searchParams] = useSearchParams();

  useEffect(() => {
    const libraryTypeParam = searchParams.get('libraryType');
    if (libraryTypeParam === 'own') {
      setLibraryType('own');
    }
  }, [searchParams, setLibraryType]);

  return (
    <>
      <Grid container spacing={3} sx={{ mt: '-78px' }}>
        <Grid size={{ xs: 12 }} sx={{ pt: '16px !important' }}>
          <Stack spacing={0} sx={{ marginBottom: '20px' }}>
            <Typography
              variant='h5'
              component='h1'
              sx={{ mb: '16px' }}
              className={styles.page_title}
            >
              {t('headers.documentTemplates')}
            </Typography>
            <Grid sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Stack direction='row' marginTop={'8px'} gap={'8px'} alignItems='center'>
                {checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_LIBRARY_READ) ? (
                  <Button
                    variant='contained'
                    className={`app-bar-link ${
                      libraryType === 'businessKitz' ? '' : ' white-variant'
                    }`}
                    onClick={() => setLibraryType('businessKitz')}
                  >
                    <AccountBalanceOutlinedIcon fontSize='medium' />
                    {t('buttons.businessKitzLibrary')}
                  </Button>
                ) : (
                  <Tooltip title={t('messages.contactAdministrator')} placement='top' arrow>
                    <Button
                      variant='contained'
                      className={`app-bar-link ${
                        libraryType === 'businessKitz' ? '' : ' white-variant'
                      }`}
                      disabled={true}
                    >
                      <AccountBalanceOutlinedIcon fontSize='medium' />
                      {t('buttons.businessKitzLibrary')}
                    </Button>
                  </Tooltip>
                )}
                {checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_TEMPLATE_READ) ? (
                  <Button
                    variant='contained'
                    className={`app-bar-link ${libraryType === 'own' ? '' : ' white-variant'}`}
                    onClick={() => {
                      setLibraryType('own');
                      handleTabChange('All');
                      setCardView(true);
                      setWorkflow(null);
                    }}
                  >
                    <OwnLibraryIcon />
                    {t('buttons.ownLibrary')}
                  </Button>
                ) : (
                  <Tooltip title={t('messages.contactAdministrator')} placement='top' arrow>
                    <Button
                      variant='contained'
                      className={`app-bar-link ${libraryType === 'own' ? '' : ' white-variant'}`}
                      disabled={true}
                    >
                      <OwnLibraryIcon />
                      {t('buttons.ownLibrary')}
                    </Button>
                  </Tooltip>
                )}
                <PreviewTooltip title={<Preview />} arrow placement='top'>
                  <InfoIcon color='primary' fontSize='small' />
                </PreviewTooltip>
              </Stack>
              <Stack
                direction='row'
                marginTop={'8px'}
                gap={'8px'}
                flexWrap='wrap'
                alignItems='center'
                justifyContent='flex-end'
                width={'55%'}
              >
                {activeTab === 'Employment' && (
                  <Tooltip
                    title={t('employmentTemplatesInfo.employmentDocumentGuidance')}
                    placement='top'
                    arrow
                  >
                    <Button
                      variant='contained'
                      className='app-bar-link white-variant min-width-inherit'
                      onClick={() => {
                        setEmploymentDataMessage(true);
                      }}
                    >
                      <PriorityHighIcon fontSize='medium' />
                    </Button>
                  </Tooltip>
                )}
                <TextField
                  label={t('labels.searchByName')}
                  className='search-input'
                  slotProps={{
                    inputLabel: { style: { backgroundColor: '#f4f9fd' } },
                    input: {
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            disabled={!searchStringInputValue.trim() || libraryType === 'own'}
                            onClick={() => resetSearchFilter()}
                          >
                            <CloseIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                  value={searchStringInputValue}
                  onChange={(e) => {
                    setSearchStringInputValue(e.target.value);
                    if (e.target.value.trim().length > 0) {
                      setSearchCleared(false);
                    }
                  }}
                  disabled={libraryType === 'own'}
                  onKeyUp={(e) => {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    if (e.target.value.trim() === '' && !searchCleared) {
                      resetSearchFilter();
                    }
                  }}
                />
                {libraryType === 'businessKitz' &&
                  checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_LIBRARY_READ) && (
                    <Box className={styles.card_list_view_container}>
                      <button
                        className={`${styles.variant_btn} + ${
                          cardView ? styles.variant_btn_active : ''
                        }`}
                        onClick={() => setCardView(true)}
                      >
                        <DashboardIcon />
                        <span>{t('buttons.card')}</span>
                      </button>
                      <button
                        onClick={() => {
                          setCardView(false);
                          setWorkflow(null);
                        }}
                        className={`${styles.variant_btn} + ${
                          !cardView ? styles.variant_btn_active : ''
                        }`}
                      >
                        <BallotIcon />
                        <span>{t('buttons.list')}</span>
                      </button>
                    </Box>
                  )}
                <Box className='region-select-box'>
                  <ButtonDropDown
                    value={region}
                    onChange={(event) => setRegion(event.target.value as 'uk' | 'au' | 'global')}
                    options={regionsOptions}
                    disabled={
                      libraryType === 'own' ||
                      !checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_LIBRARY_READ)
                    }
                    ariaLabel={t('labels.region')}
                  />
                </Box>
                {checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_LIBRARY_READ) && (
                  <Tooltip title={t('buttons.documentTerms')} placement='top' arrow>
                    <Button
                      variant='contained'
                      className='app-bar-link white-variant min-width-inherit'
                      onClick={() => {
                        setDisclaimerMessage(true);
                      }}
                    >
                      <InfoOutlinedIcon fontSize='medium' />
                    </Button>
                  </Tooltip>
                )}
              </Stack>
            </Grid>
          </Stack>
          <Box className={styles.document_header}>
            <Box className={styles.actions_group}>
              {/*<Pagination page={page} totalPages={1} onPageChange={handlePageChange} />
              <SearchField
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder='Search for document'
              />
              <FilterDrawer sections={filterSections} onApplyFilters={handleApplyFilters} />*/}
            </Box>
          </Box>

          <Box className={styles.tab_container}>
            <Box className={styles.tabs_section}>
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => handleTabChange(newValue)}
                aria-label='document categories'
                className={styles.tabs}
              >
                <Tab label='All' value='All' />
                <Tab label='Business' value='Business' /*disabled={libraryType === 'own'}*/ />
                <Tab label='Employment' value='Employment' /*disabled={libraryType === 'own'}*/ />
                <Tab label='Customers' value='Customers' /*disabled={libraryType === 'own'}*/ />
                <Tab label='Suppliers' value='Suppliers' /*disabled={libraryType === 'own'}*/ />
              </Tabs>
            </Box>
          </Box>

          <Box sx={{ mt: '15px', position: 'relative' }}>
            {cardView && libraryType === 'businessKitz' ? (
              <>
                <CardViewContent
                  activeTab={activeTab}
                  changeTabFunction={handleTabChange}
                  templates={libraryItems}
                  setWorkflow={setWorkflow}
                  workflow={workflow}
                />
              </>
            ) : (
              <LibraryList
                libraryItems={
                  libraryType === 'own'
                    ? [...documentTemplates].sort((a, b) => sortByDate(a, b))
                    : libraryItems
                }
                ownTemplate={libraryType === 'own' ? true : false}
                category={activeTab}
              />
            )}
            {(libraryItemsPendingStatus ||
              disclaimerInfoLoadingStatus === 'loading' ||
              documentTemplatesLoading === 'loading' ||
              employeesLoading === 'loading' ||
              customersLoading === 'loading' ||
              suppliersLoading === 'loading') && (
              <ProgressOverlay className={'position-absolute'} />
            )}
          </Box>
        </Grid>
      </Grid>
      {employmentDataMessage && (
        <MessageModal
          open={employmentDataMessage}
          modalTitle={t('employmentTemplatesInfo.employmentDocumentGuidance')}
          modalContent={<EmploymentInfoText />}
          headerVariant={'h5'}
          goBackButtonText={t('buttons.close')}
          noActionButton={true}
          onClose={() => {
            setEmploymentDataMessage(false);
          }}
        />
      )}
      {disclaimerMessage && (
        <MessageModal
          open={disclaimerMessage}
          modalTitle={t('headers.documentTerms')}
          modalContent={<DisclaimerText />}
          actionButtonText={t('buttons.accept')}
          headerVariant={'h5'}
          actionButtonFn={() => {
            dispatch(acceptDisclaimer())
              .unwrap()
              .finally(() => {
                setDisclaimerMessage(false);
              });
          }}
          goBackButtonText={t('buttons.goBack')}
          onClose={() => {
            setDisclaimerMessage(false);
          }}
        />
      )}
      {disclaimerStatus === 'loadingAccepting' && <ProgressOverlay />}
    </>
  );
};

export default Library;
